import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { EuiButton, EuiPageHeader, EuiTitle, EuiToolTip } from '@equipmentshare/ds2'
import { heapEvent } from '@equipmentshare/heaper'
import { useTransferOrderQuery, TransferOrderStatus, TransferOrderType, Permission, ReservationTargetType, AssetTransferStatus } from '../../graphql'
import { formatDateTime } from '../../utils/formatters/date'
import PermissionsCheck from '../../components/PermissionsCheck'
import useCanCancelTransferOrder from './hooks/useCanCancelTransferOrder'
import CancelTransferOrderModal from './components/CancelTransferOrderModal'
import { cancelRestrictedTooltip } from '../../constants/transfer-cancellation'
import { CANCELLABLE_STATUSES, STATUS_CHIP_COLOR, STATUS_LABEL } from './constants'
import ApproveRejectTransferOrderModal from './components/ApproveRejectTransferOrderModal'
import AssetTransferPanel, { AssetTransfer } from './components/AssetTransferPanel'


function formatAllocationTarget( type?: ReservationTargetType | null, id?: string | null ): string {
	if ( !type || !id ) {
		return 'Stock'
	}
	switch ( type ) {
		case ReservationTargetType.WorkOrder: return `WO #${id}`
		case ReservationTargetType.Invoice: return `INV #${id}`
		case ReservationTargetType.Order: return `ORD #${id}`
		case ReservationTargetType.FulfillmentOrder: return `Fulfillment #${id}`
		default: return `${type} #${id}`
	}
}

type InfoRowProps = {
	label: string,
	value: string | null | undefined,
}

function InfoRow( { label, value }: InfoRowProps ) {
	if ( value === null || value === '' ) {
		return null
	}
	return (
		<Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
			<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, flexShrink: 0 }}>
				{label}
			</Typography>
			<Typography variant="body2">
				{value}
			</Typography>
		</Stack>
	)
}

export default function TransferOrderDetails() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const [ showCancelModal, setShowCancelModal ] = useState( false )
	const [ showApproveModal, setShowApproveModal ] = useState( false )
	const [ showRejectModal, setShowRejectModal ] = useState( false )
	const [ receiptPopoverAnchor, setReceiptPopoverAnchor ] = useState<HTMLElement | null>( null )
	const [ receiptPopoverData, setReceiptPopoverData ] = useState<{ qty: number, receivedBy: string, dateReceived: string }[]>( [] )

	const { data, loading, error } = useTransferOrderQuery( {
		variables: { transferOrderId: id ?? '' },
		skip: !id,
		fetchPolicy: 'network-only',
	} )

	useEffect( () => {
		if ( id ) {
			heapEvent( 'Inventory - Transfer Orders - View Details - Open', { transfer_order_id: id } )
		}
	}, [ id ] )

	const order = data?.transferOrder
	const isManual = order?.transferOrderType === TransferOrderType.Manual
	const isReceivable = order?.status === TransferOrderStatus.Approved || order?.status === TransferOrderStatus.InTransit
	const isCancellable = order ? CANCELLABLE_STATUSES.has( order.status ) : false
	const isApprovable = isManual && order?.status === TransferOrderStatus.Pending
	// Scoped to from_store: the API rejects a cancel from the receiving side.
	const { canCancel, blockedAsReceiver } = useCanCancelTransferOrder( order?.fromStore?.id, order?.toStore?.id )

	const handleBackClick = () => navigate( '/transfer-orders' )
	const handleReceiveClick = () => {
		heapEvent( 'Inventory - Transfer Orders - Details - Click - Receive Items', { transfer_order_id: id ?? '' } )
		navigate( `/transfer-orders/${id}/receive` )
	}
	const handleCancelClick = () => {
		heapEvent( 'Inventory - Transfer Orders - Details - Click - Cancel', { transfer_order_id: id ?? '' } )
		setShowCancelModal( true )
	}
	const handleCancelSuccess = () => {
		setShowCancelModal( false )
	}
	const handleApproveClick = () => {
		heapEvent( 'Inventory - Transfer Orders - Details - Click - Approve', { transfer_order_id: id ?? '' } )
		setShowApproveModal( true )
	}
	const handleRejectClick = () => {
		heapEvent( 'Inventory - Transfer Orders - Details - Click - Reject', { transfer_order_id: id ?? '' } )
		setShowRejectModal( true )
	}
	const handleApprovalSuccess = () => {
		setShowApproveModal( false )
		setShowRejectModal( false )
	}

	const rightSideItems = [
		...( isCancellable && canCancel ? [
			<EuiButton color="danger" key="cancel" onClick={handleCancelClick}>
				Cancel Order
			</EuiButton>,
		] : [] ),
		// Receiving-side viewers get the button greyed out with an explanation rather
		// than a missing button, so the restriction reads as deliberate.
		...( isCancellable && blockedAsReceiver ? [
			<EuiToolTip content={cancelRestrictedTooltip( !!order?.fromStore?.isWarehouse )} key="cancel-blocked" position="bottom">
				<EuiButton color="danger" disabled>
					Cancel Order
				</EuiButton>
			</EuiToolTip>,
		] : [] ),
		...( isApprovable ? [
			<PermissionsCheck key="reject" permissionsToCheck={[ Permission.CanApprovePartsTransferByStoreId ]}>
				<EuiButton color="danger" onClick={handleRejectClick}>
					Reject
				</EuiButton>
			</PermissionsCheck>,
			<PermissionsCheck key="approve" permissionsToCheck={[ Permission.CanApprovePartsTransferByStoreId ]}>
				<EuiButton fill onClick={handleApproveClick}>
					Approve
				</EuiButton>
			</PermissionsCheck>,
		] : [] ),
		...( isReceivable ? [
			<PermissionsCheck key="receive" permissionsToCheck={[ Permission.CanCompletePartsTransferByStoreId ]}>
				<EuiButton fill onClick={handleReceiveClick}>
					Receive Items
				</EuiButton>
			</PermissionsCheck>,
		] : [] ),
	]

	const receiptsByLineItem = ( order?.receipts ?? [] ).reduce<Record<string, { qty: number, receivedBy: string, dateReceived: string }[]>>(
		( acc, receipt ) => {
			const receivedBy = [ receipt.receivedBy.firstName, receipt.receivedBy.lastName ].filter( Boolean ).join( ' ' )
			receipt.receiptItems.forEach( ( item ) => {
				if ( !acc[ item.lineItemId ] ) {
					acc[ item.lineItemId ] = []
				}
				acc[ item.lineItemId ].push( { qty: item.quantityReceived, receivedBy, dateReceived: receipt.dateReceived } )
			} )
			return acc
		},
		{}
	)

	return (
		<Box>
			{/* Header */}
			<EuiPageHeader
				pageTitle={<EuiTitle size="m"><span>TO-{id}</span></EuiTitle>}
				paddingSize="l"
				bottomBorder="extended"
				rightSideItems={rightSideItems.length > 0 ? rightSideItems : undefined}
				breadcrumbs={[
					{ text: 'Transfer Orders',
						href: '/transfer-orders',
						onClick: ( e ) => {
							e.preventDefault();navigate( '/transfer-orders' )
						} },
					{ text: 'Transfer Order Details' },
				]}
			/>

			<Box sx={{ p: 3 }}>
				{/* Loading */}
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
						<CircularProgress />
					</Box>
				)}

				{/* Error */}
				{( error || ( !loading && !order ) ) && (
					<Alert
						severity="error"
						action={
							<Button color="inherit" size="small" onClick={handleBackClick}>
								Back to Transfer Orders
							</Button>
						}
					>
						{error?.message ?? 'Transfer order not found.'}
					</Alert>
				)}

				{/* Content */}
				{order && (
					<Stack spacing={3}>
						{/* Side-by-side panels */}
						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
							{/* Left panel: Transfer Info */}
							<Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
								<Typography variant="h6" fontWeight="bold" gutterBottom>
								Transfer
								</Typography>
								<Stack direction="row" spacing={1} sx={{ mb: 1, py: 0.5 }} alignItems="center">
									<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, flexShrink: 0 }}>
									Status
									</Typography>
									<Chip
										label={STATUS_LABEL[ order.status ]}
										color={STATUS_CHIP_COLOR[ order.status ]}
										size="small"
									/>
								</Stack>
								<InfoRow label="Transfer Order Number" value={`TO-${id}`} />
								<InfoRow label="Transfer To" value={order.toStore?.name} />
								<InfoRow label="Created At" value={formatDateTime( order.dateCreated )} />
								{'requester' in order ? (
									<>
										<InfoRow
											label="Requested By"
											value={[ order.requester.firstName, order.requester.lastName ].filter( Boolean ).join( ' ' ) || null}
										/>
										<InfoRow label="Requester Note" value={order.requesterNote ?? '—'} />
									</>
								) : null}
							</Paper>

							{/* Right panel: Fulfillment Info */}
							<Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
								<Typography variant="h6" fontWeight="bold" gutterBottom>
								Fulfillment
								</Typography>
								<InfoRow label="Transfer From" value={order.fromStore?.name} />
								{'approver' in order ? (
									<InfoRow
										label="Approved By"
										value={order.approver ? [ order.approver.firstName, order.approver.lastName ].filter( Boolean ).join( ' ' ) : null}
									/>
								) : null}
								{'dateApproved' in order ? (
									<InfoRow label="Approved At" value={formatDateTime( order.dateApproved )} />
								) : null}
								<InfoRow label="Shipped At" value={formatDateTime( order.dateShipped )} />
								<InfoRow label="Tracking Number" value={order.trackingNumber ?? 'N/A'} />
								{order.demandRequest && (
									<Stack direction="row" spacing={2} sx={{ py: 0.5 }} alignItems="center">
										<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160, flexShrink: 0 }}>
											Request
										</Typography>
										<Typography variant="body2">
											<Link to={`/requests/${order.demandRequest.id}`}>
												REQ-{order.demandRequest.requestNumber}
											</Link>
										</Typography>
									</Stack>
								)}
							</Paper>
						</Stack>

						{/* Line Items */}
						<Box>
							<Typography variant="h6" gutterBottom>
								Items{' '}
								<Typography component="span" variant="body1" color="text.secondary">
									({( order.lineItems ?? [] ).reduce( ( sum, item ) => sum + ( item.quantityRequested ?? 0 ), 0 )} Items)
								</Typography>
							</Typography>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>MFR #</TableCell>
											<TableCell>Manufacturer</TableCell>
											<TableCell>Description</TableCell>
											<TableCell>Item is For</TableCell>
											<TableCell>Note</TableCell>
											<TableCell align="right">Qty Requested</TableCell>
											<TableCell align="right">Qty Received / Qty Cancelled</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{( order.lineItems ?? [] ).length === 0 ? (
											<TableRow>
												<TableCell colSpan={7} align="center">
													<Typography variant="body2" color="text.secondary">
														No line items
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											( order.lineItems ?? [] ).map( ( item ) => {
												return (
													<TableRow key={item.lineItemId}>
														<TableCell>{item.product?.manufacturerNumber ?? '—'}</TableCell>
														<TableCell>{item.product?.manufacturer?.name ?? '—'}</TableCell>
														<TableCell>{item.product?.name ?? item.productId}</TableCell>
														<TableCell>{formatAllocationTarget( item.allocationTargetType, item.allocationTargetId )}</TableCell>
														<TableCell>{item.lineNote ?? '—'}</TableCell>
														<TableCell align="right">{item.quantityRequested}</TableCell>
														<TableCell align="right">
															{receiptsByLineItem[ item.lineItemId ]?.length > 0 ? (
																<span
																	style={{ cursor: 'default', borderBottom: '1px dashed currentColor' }}
																	onMouseEnter={( e ) => {
																		setReceiptPopoverAnchor( e.currentTarget )
																		setReceiptPopoverData( receiptsByLineItem[ item.lineItemId ] )
																	}}
																	onMouseLeave={() => setReceiptPopoverAnchor( null )}
																>
																	{item.quantityReceived ?? '—'} / —
																</span>
															) : (
																<>{item.quantityReceived ?? '—'} / —</>
															)}
														</TableCell>
													</TableRow>
												)
											} )
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>

						{/* Asset Transfers (SystemTransferOrder only) */}
						{( () => {
							const systemOrder = order as unknown as {
								assetTransferStatus?: AssetTransferStatus | null,
								assetTransactions?: AssetTransfer[],
							}
							if ( !systemOrder.assetTransferStatus ) return null
							return (
								<AssetTransferPanel
									transferOrderId={String( order.id )}
									assetTransferStatus={systemOrder.assetTransferStatus}
									assetTransactions={systemOrder.assetTransactions ?? []}
								/>
							)
						} )()}

					</Stack>
				)}

				<Popover
					open={Boolean( receiptPopoverAnchor )}
					anchorEl={receiptPopoverAnchor}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					transformOrigin={{ vertical: 'top', horizontal: 'center' }}
					onClose={() => setReceiptPopoverAnchor( null )}
					disableRestoreFocus
					sx={{ pointerEvents: 'none' }}
				>
					<Box sx={{ p: 2, minWidth: 200 }}>
						<Typography variant="subtitle2" gutterBottom>Received By</Typography>
						{receiptPopoverData.map( ( r, i ) => (
							<Box key={i} sx={{ mt: i > 0 ? 1.5 : 0 }}>
								<Typography variant="body2" fontWeight="medium">{r.receivedBy}</Typography>
								<Typography variant="body2" color="text.secondary">{formatDateTime( r.dateReceived )}</Typography>
							</Box>
						) )}
					</Box>
				</Popover>
			</Box>

			{showCancelModal && id && (
				<CancelTransferOrderModal
					transferOrderId={id}
					onClose={() => setShowCancelModal( false )}
					onSuccess={handleCancelSuccess}
				/>
			)}
			{showApproveModal && id && (
				<ApproveRejectTransferOrderModal
					transferOrderId={id}
					action="approve"
					onClose={() => setShowApproveModal( false )}
					onSuccess={handleApprovalSuccess}
				/>
			)}
			{showRejectModal && id && (
				<ApproveRejectTransferOrderModal
					transferOrderId={id}
					action="reject"
					onClose={() => setShowRejectModal( false )}
					onSuccess={handleApprovalSuccess}
				/>
			)}
		</Box>
	)
}
